import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Paper,
  Chip,
  Grid
} from '@mui/material';
import {
  ArrowBack,
  Psychology,
  Person,
  Star,
  Schedule,
  Videocam,
  Phone,
  Chat,
  Favorite,
  AutoAwesome
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const HumanTherapists = () => {
  const navigate = useNavigate();

  const comingSoonFeatures = [
    {
      icon: <Person />,
      title: "Licensed Therapists",
      description: "Connect with certified mental health professionals"
    },
    {
      icon: <Videocam />,
      title: "Video Sessions",
      description: "Face-to-face therapy sessions from home"
    },
    {
      icon: <Phone />,
      title: "Phone Consultations",
      description: "Flexible scheduling for phone sessions"
    },
    {
      icon: <Chat />,
      title: "Text Therapy",
      description: "Ongoing support through secure messaging"
    },
    {
      icon: <Schedule />,
      title: "Flexible Scheduling",
      description: "Book sessions that fit your schedule"
    },
    {
      icon: <Favorite />,
      title: "Personalized Matching",
      description: "Get matched with the right therapist for you"
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3 } }}>
      {/* Dopamine-producing Hero Header */}
      <Paper 
        sx={{ 
          p: { xs: 4, sm: 6 }, 
          mb: { xs: 4, sm: 6 }, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 4,
          position: 'relative',
          overflow: 'hidden',
          textAlign: 'center'
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'flex-start', 
            mb: 4 
          }}>
            <IconButton 
              onClick={() => navigate('/conversation')} 
              sx={{ color: 'white', mr: 2 }}
            >
              <ArrowBack />
            </IconButton>
          </Box>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Box sx={{ mb: 4 }}>
              <Psychology sx={{ 
                fontSize: { xs: 60, sm: 80 }, 
                mb: 2, 
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))' 
              }} />
              <Typography variant="h2" sx={{ 
                fontWeight: 'bold', 
                mb: 2,
                fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
                background: 'linear-gradient(45deg, #FFD700, #FFA500)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 4px 8px rgba(0,0,0,0.3)'
              }}>
                Human Therapists
              </Typography>
              
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                <Chip
                  label="✨ COMING SOON! ✨"
                  sx={{
                    bgcolor: 'rgba(255, 215, 0, 0.2)',
                    color: '#FFD700',
                    fontWeight: 'bold',
                    fontSize: '1.5rem',
                    px: 4,
                    py: 2,
                    height: 'auto',
                    border: '2px solid rgba(255, 215, 0, 0.3)',
                    boxShadow: '0 8px 32px rgba(255, 215, 0, 0.2)',
                    animation: 'gentlePulse 2s infinite',
                    pointerEvents: 'none'
                  }}
                />
              </motion.div>
            </Box>
          </motion.div>
          
          <Typography variant="h5" sx={{ 
            mb: 3, 
            opacity: 0.95,
            fontWeight: 600
          }}>
            🌟 Connect with Licensed Mental Health Professionals
          </Typography>
          
          <Typography variant="body1" sx={{ 
            fontSize: '1.2rem',
            opacity: 0.9,
            maxWidth: 600,
            mx: 'auto',
            lineHeight: 1.6
          }}>
            We're working hard to bring you access to qualified human therapists 
            who can provide personalized, professional mental health support.
          </Typography>
        </Box>
        
        {/* Animated background elements */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
        }} />
        
        {/* Floating decorative elements */}
        <motion.div
          animate={{
            y: [0, -10, 0],
            rotate: [0, 5, 0]
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{
            position: 'absolute',
            top: '20%',
            left: '10%',
            opacity: 0.3
          }}
        >
          <AutoAwesome sx={{ fontSize: 40, color: '#FFD700' }} />
        </motion.div>
        
        <motion.div
          animate={{
            y: [0, 10, 0],
            rotate: [0, -5, 0]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{
            position: 'absolute',
            top: '30%',
            right: '15%',
            opacity: 0.3
          }}
        >
          <Star sx={{ fontSize: 35, color: '#FFD700' }} />
        </motion.div>
      </Paper>

      {/* Features Preview */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h4" sx={{ 
          textAlign: 'center', 
          mb: 4, 
          fontWeight: 'bold',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          🚀 What's Coming
        </Typography>
        
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {comingSoonFeatures.map((feature, index) => (
            <Grid item xs={12} sm={6} md={6} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
              >
                <Card sx={{
                  height: '100%',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease-in-out',
                  pointerEvents: 'none',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 16px 48px rgba(0,0,0,0.15)',
                  }
                }}>
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: 2,
                      flexDirection: { xs: 'column', sm: 'row' },
                      textAlign: { xs: 'center', sm: 'left' },
                      gap: { xs: 1, sm: 0 }
                    }}>
                      <Box sx={{
                        p: 1.5,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        mr: { xs: 0, sm: 2 }
                      }}>
                        {feature.icon}
                      </Box>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 'bold',
                        fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' }
                      }}>
                        {feature.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Call to Action */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 3, color: 'text.secondary' }}>
          In the meantime, continue your wellness journey with our AI Wellness Rep
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/conversation')}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            px: 4,
            py: 2,
            fontSize: '1.1rem',
            fontWeight: 'bold',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)',
            },
            transition: 'all 0.3s ease-in-out'
          }}
        >
          Continue with AI Wellness Rep
        </Button>
      </Box>
    </Container>
  );
};

export default HumanTherapists;
